// overlayKit/BigStat.tsx — count-up that locks on the spoken value word.
// Migrated from ChocolateFullPreview inline BigStat. Palette via context.
// `t = frame / fps` (no Sequence wrapper) — render this directly inside the
// production's <AbsoluteFill>; the card self-fades outside [labelS, outS].
import React from "react";
import { useCurrentFrame, useVideoConfig, interpolate, spring } from "remotion";
import { appear, clamp, FONT, TS, win } from "./utils";
import { useColor } from "./palette";

export type BigStatProps = {
  /** Small caps line above the number. Omit for number-only (Yash default 2026-06-07). */
  label?: string;
  value: number;
  valueS: number;
  outS: number;
  endS?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  sub?: string;
  /** Single accent color (label + suffix). Overrides labelColor when set. Default: secondary. */
  color?: string;
  /** Optional fine-grained palette tokens. */
  labelColor?: string;
  numberColor?: string;
  subColor?: string;
  /** Screen position. Default "top-left" (legacy). "right-mid" = right edge, vertically centered. */
  pos?: "top-left" | "right-mid";
  /** When set, the number gets a colored glow (e.g. "#ff2a2a" for red-glowing hero stats). */
  glow?: string;
  /** Hero-number font size in px. Default 110. prefix/suffix scale proportionally. */
  numberSize?: number;
};

export const BigStat: React.FC<BigStatProps> = ({
  label, value, valueS, outS, endS, decimals = 0, prefix, suffix, sub,
  color, labelColor, numberColor = "neutral", subColor = "primary",
  pos = "top-left", glow, numberSize = 78,
}) => {
  const accent = color ?? labelColor ?? "secondary";
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = frame / fps;
  const labelS = valueS - 0.25;
  const op = win(t, labelS, outS);
  if (op <= 0.001) return null;

  // Count animation: if endS provided, lock on word-end (factory-overlays Rule 2);
  // else fall back to legacy 0.42s ramp.
  const lockEnd = endS ?? valueS + 0.42;
  const cur = interpolate(t, [valueS, lockEnd], [0, value], clamp);
  const numOp = appear(t, valueS, 0.12);
  const sc = spring({ frame: Math.max(0, frame - Math.round(valueS * fps)), fps, config: { damping: 13, stiffness: 95 } });
  const subOp = appear(t, valueS + 0.5, 0.3);
  const shown = value >= 100 ? Math.round(cur).toLocaleString() : cur.toFixed(decimals);

  const labelHex = useColor(accent);
  const numHex = useColor(numberColor);
  const subHex = useColor(subColor);

  // Container position: legacy top-left, or right-edge vertically-centered.
  const posStyle: React.CSSProperties =
    pos === "right-mid"
      ? { position: "absolute", right: 22, top: "50%", transform: "translateY(-50%)", textAlign: "right" }
      : { position: "absolute", top: 18, left: 22, textAlign: "left" };
  const numOrigin = pos === "right-mid" ? "right center" : "left bottom";
  const flexJustify = pos === "right-mid" ? "flex-end" : "flex-start";
  // Red (or any) glow halo on the hero number.
  const numShadow = glow
    ? `0 0 26px ${glow}, 0 0 54px ${glow}cc, 0 0 12px ${glow}, ${TS}`
    : TS;

  return (
    <div style={{ ...posStyle, fontFamily: FONT, opacity: op }}>
      {label && <div style={{ color: labelHex, fontSize: 25, letterSpacing: "0.24em", marginBottom: 14, textShadow: TS, opacity: appear(t, labelS, 0.2) }}>{label}</div>}
      <div style={{ display: "flex", justifyContent: flexJustify, alignItems: "baseline", gap: 6, opacity: numOp, transform: `scale(${sc})`, transformOrigin: numOrigin }}>
        {prefix && <span style={{ color: numHex, fontSize: Math.round(numberSize * 0.47), fontWeight: 800, textShadow: numShadow }}>{prefix}</span>}
        <span style={{ color: numHex, fontSize: numberSize, fontWeight: 800, letterSpacing: "-3px", textShadow: numShadow, lineHeight: 1 }}>{shown}</span>
        {suffix && <span style={{ color: labelHex, fontSize: Math.round(numberSize * 0.53), fontWeight: 700, textShadow: numShadow }}>{suffix}</span>}
      </div>
      {sub && <div style={{ color: subHex, fontSize: 21, letterSpacing: "0.16em", marginTop: 20, textShadow: TS, opacity: subOp }}>{sub}</div>}
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
